import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type { ViewToken } from 'react-native';
import { List, type ListRef } from '@banhala/ui-kit';
import type { DescriptionCardCarouselProps, DescriptionCardItem } from './types';
import { DescriptionCard } from './components/DescriptionCard';
import { isNil } from '../../../../utils';
import { useWebDragToScroll } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import type { AnyObject } from '../../../../types';

const PADDING_HORIZONTAL = 12;
const GAP = 4;

type CardEntity = { item: DescriptionCardItem; logging: AnyObject };

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 1 };

const keyExtractor = (_item: CardEntity, index: number) => index.toString();

const renderItem = ({ item }: { item: CardEntity }) => (
  <DescriptionCard item={item.item} logging={item.logging} />
);

export const DescriptionCardCarousel = ({
  entity,
  isVisible,
  isAwaitingDataChange,
}: DescriptionCardCarouselProps) => {
  const listRef = useRef<ListRef<CardEntity>>(null);
  useWebDragToScroll(listRef);
  // GoodsCarousel.useLogDisplayGoods 패턴 — inner FlatList 가 부모(AtomicRenderer)
  // viewport 와 무관하게 viewable 을 보고하므로, emit 시점에 부모 가시성으로 가드 +
  // isVisible false→true 전이 시 캐시 기반 재emit 으로 노출 결손 방지.
  // DescriptionCardItem 에 sno 가 없어 index 기반 dedup 유지.
  const displayedRef = useRef(new Set<number>());
  const lastViewableItemsRef = useRef<ViewToken[]>([]);
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;

  const logAnalyticsEvent = useLogAnalyticsEvent();

  const emitForViewable = useCallback(
    (viewableItems: ViewToken[]) => {
      if (isVisibleRef.current === false) return;
      if (isAwaitingDataChangeRef.current === true) return;
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        const cardData = item as CardEntity;
        logAnalyticsEvent({
          name: 'DISPLAY_DESCRIPTION_CARD',
          analytics: cardData.logging.analytics,
          ably_only_log: cardData.logging.ably_only_log,
        });
      });
    },
    [logAnalyticsEvent],
  );

  useEffect(() => {
    if (isVisible && !isAwaitingDataChange) {
      emitForViewable(lastViewableItemsRef.current);
    }
  }, [isVisible, isAwaitingDataChange, emitForViewable]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      lastViewableItemsRef.current = viewableItems;
      emitForViewable(viewableItems);
    },
    [emitForViewable],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List<CardEntity>
      ref={listRef}
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: PADDING_HORIZONTAL,
    gap: GAP,
  },
});
